/**
 * Shared flight fare helpers — mirrors travel_app preview rules:
 * UI sticker = Gross; CreateItinerary NetAmount = Net; hide supplier commission.
 */

export const formatInr = (amount) => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0)
  } catch {
    return `₹${Math.round(Number(amount) || 0)}`
  }
}

/** Gross sticker airfare (never Net). */
export const stickerAirfare = (summary = {}, fallbackNet = 0) => {
  const s = summary && typeof summary === 'object' ? summary : {}
  const base = Number(s.base_fare) || 0
  const taxField = Number(s.taxes_and_fees) || 0
  const taxFromCharges = (Array.isArray(s.tax_charges) ? s.tax_charges : [])
    .reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
  const tax = taxField > 0 ? taxField : taxFromCharges
  const gross = Number(s.gross_fare ?? s.gross_amount)
  let others = Number(s.others)
  if (
    !Number.isFinite(others)
    && Number.isFinite(gross)
    && gross > 0
    && (base > 0 || tax > 0)
  ) {
    others = Math.round((gross - base - tax) * 100) / 100
  }
  if (!Number.isFinite(others)) others = 0
  if (Number.isFinite(gross) && gross > 0) {
    return Math.round(gross * 100) / 100
  }
  if (base > 0 || tax > 0) {
    return Math.round((base + tax + others) * 100) / 100
  }
  const total = Number(s.total)
  const net = Number(s.net_fare) || Number(fallbackNet) || 0
  if (Number.isFinite(total) && total > 0) {
    if (!(net > 0) || Math.abs(total - net) > 1) {
      return Math.round(total * 100) / 100
    }
  }
  return net > 0 ? Math.round(net * 100) / 100 : 0
}

/** Fare review card: prefer priced API summary; until then search list price — never ₹0 from empty summary. */
export const fareCardPriceLabel = (fare = {}, searchFare = {}, formatMoneyFn = formatInr) => {
  const f = fare && typeof fare === 'object' ? fare : {}
  const s = searchFare && typeof searchFare === 'object' ? searchFare : {}
  if (f.fare_summary?.gross_fare_label) return String(f.fare_summary.gross_fare_label)
  if (f.fare_summary?.total_label) return String(f.fare_summary.total_label)
  const sticker = stickerAirfare(f.fare_summary || {})
  if (sticker > 0) return formatMoneyFn(sticker)
  if (s.price_label) return String(s.price_label)
  const searchAmt = Number(s.price ?? s.gross_fare)
  if (Number.isFinite(searchAmt) && searchAmt > 0) return formatMoneyFn(searchAmt)
  return ''
}

/** Supplier Net for CreateItinerary / orders NetAmount. */
export const shownNetAmount = (summary = {}, fallbackNet = 0) => {
  const s = summary && typeof summary === 'object' ? summary : {}
  const net = Number(s.net_fare)
  if (Number.isFinite(net) && net > 0) return Math.round(net * 100) / 100
  const fallback = Number(fallbackNet)
  return Number.isFinite(fallback) && fallback > 0
    ? Math.round(fallback * 100) / 100
    : 0
}

/** Others = Gross − (Base + Tax); 0 when aligned. */
export const resolveOthers = (summary = {}) => {
  const s = summary && typeof summary === 'object' ? summary : {}
  let others = Number(s.others)
  if (Number.isFinite(others)) return Math.round(others * 100) / 100
  const base = Number(s.base_fare) || 0
  const tax = Number(s.taxes_and_fees) || 0
  const gross = stickerAirfare(s)
  if (gross > 0 && (base > 0 || tax > 0)) {
    return Math.round((gross - base - tax) * 100) / 100
  }
  return 0
}

const asNumber = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const TRIP_PAYMENT_FARE_KEYS = new Set([
  'base_fare',
  'taxes_and_fees',
  'others',
  'services',
  'convenience_fee',
  'discount',
  'total_amount',
])

const normalizeTripFareLabel = (label, key) => {
  if (key === 'base_fare') return 'Base fare'
  if (key === 'taxes_and_fees') return 'Taxes & fees'
  if (key === 'others') return 'Others'
  if (key === 'services') return 'Baggage, meals & seats'
  if (key === 'convenience_fee') return 'Convenience fee'
  if (key === 'total_amount') return 'Total'
  return label || ''
}

/**
 * My Trips Payment Details card — same shape as travel_app trips-preview (prev UI).
 * Fare lines only; wallet/gateway sit under Paid amount in pay meta.
 */
export const buildFlightTripPaymentFareLines = (booking = {}, formatMoneyFn = formatInr) => {
  const fare = booking.fare_breakup || {}
  const receiptLines = Array.isArray(fare.summary_lines)
    ? fare.summary_lines
    : (Array.isArray(fare.lines) ? fare.lines : [])
  if (receiptLines.length) {
    const filtered = receiptLines.filter((row) => {
      const key = String(row.key || '')
      if (key && !TRIP_PAYMENT_FARE_KEYS.has(key)) return false
      return true
    })
    if (filtered.length) {
      return filtered.map((row) => ({
        label: normalizeTripFareLabel(row.label, row.key),
        value: row.amount_label || formatMoneyFn(row.amount),
        total: row.kind === 'total' || row.key === 'total_amount',
        subtotal: row.kind === 'subtotal',
        discount: Boolean(row.deduction),
        sub: Boolean(row.sub),
      }))
    }
  }
  return buildFlightFareReceiptLines(booking, formatMoneyFn).filter(
    (line) => line.label !== 'Paid online',
  )
}

/**
 * Order / My Trips receipt lines — mirrors travel_app static/flights/trips-preview.js.
 * Prefers API ``fare_breakup.summary_lines`` when present.
 */
export const buildFlightFareReceiptLines = (booking = {}, formatMoneyFn = formatInr) => {
  const fare = booking.fare_breakup || {}
  const receiptLines = Array.isArray(fare.summary_lines)
    ? fare.summary_lines
    : (Array.isArray(fare.lines) ? fare.lines : [])
  if (receiptLines.length) {
    return receiptLines.map((row) => ({
      label: row.label || '',
      value: row.amount_label || formatMoneyFn(row.amount),
      total: row.kind === 'total',
      subtotal: row.kind === 'subtotal',
      discount: Boolean(row.deduction),
      sub: Boolean(row.sub),
    }))
  }

  const coupon = booking.coupon || {}
  const hasBreakup = Boolean(fare.has_breakup)
  const others = asNumber(fare.others ?? resolveOthers(booking.fare_summary || {}))
  const addons = asNumber(fare.addons ?? booking.taxes_and_fees)
  const convenience = asNumber(fare.convenience_fee ?? booking.convenience_fee)
  const discount = asNumber(fare.discount_amount || coupon.discount_amount || booking.discount_amount)
  const wallet = asNumber(fare.wallet_amount ?? booking.wallet_amount)
  const addonLines = Array.isArray(fare.addon_lines) ? fare.addon_lines : []
  const taxCharges = Array.isArray(fare.tax_charges) ? fare.tax_charges : []
  const payment = booking.payment || {}
  const gatewayPaid = asNumber(payment.gateway_amount ?? booking.amount)
  const totalBooking = asNumber(
    fare.total_amount
    ?? booking.total_amount
    ?? (gatewayPaid + wallet),
  )
  const money = (label, fallback) => label || formatMoneyFn(fallback)

  return [
    hasBreakup
      ? { label: 'Base fare', value: fare.base_fare_label || money(null, fare.base_fare) }
      : {
        label: 'Airfare',
        value: fare.gross_fare_label
          || fare.total_amount_label
          || money(null, fare.gross_fare || fare.total_amount || booking.amount),
      },
    hasBreakup
      ? { label: 'Taxes & fees', value: fare.taxes_and_fees_label || money(null, fare.taxes_and_fees) }
      : null,
    ...(hasBreakup && others !== 0
      ? [{
        label: 'Others',
        value: fare.others_label || money(null, others),
      }]
      : []),
    ...taxCharges
      .filter((row) => asNumber(row.amount) > 0)
      .map((row) => ({
        label: row.label || 'Charge',
        value: row.amount_label || money(null, row.amount),
        sub: true,
      })),
    ...(addons > 0
      ? [{
        label: 'Baggage, meals & seats',
        value: fare.addons_label || money(null, addons),
      }]
      : []),
    ...addonLines
      .filter((row) => asNumber(row.amount) > 0)
      .map((row) => ({
        label: row.label || 'Add-on',
        value: row.amount_label || money(null, row.amount),
        sub: true,
      })),
    ...(convenience > 0
      ? [{
        label: 'Convenience fee',
        value: fare.convenience_fee_label || money(null, convenience),
      }]
      : []),
    ...(discount > 0
      ? [{
        label: coupon.code ? `Coupon (${coupon.code})` : 'Coupon',
        value: `− ${fare.discount_amount_label || money(null, discount)}`,
        discount: true,
      }]
      : []),
    ...(wallet > 0
      ? [{
        label: 'Paid via wallet',
        value: fare.wallet_amount_label || money(null, wallet),
      }]
      : []),
    {
      label: 'Total',
      value: fare.total_amount_label
        || fare.total_label
        || booking.total_amount_label
        || booking.amount_label
        || money(null, totalBooking || gatewayPaid),
      total: true,
    },
  ].filter(Boolean)
}

/** INR → razorpay only; other currencies → stripe only. */
export const resolveAllowedGateways = (currency, fromApi) => {
  if (Array.isArray(fromApi) && fromApi.length) {
    return fromApi.map((g) => String(g || '').toLowerCase()).filter(Boolean)
  }
  return String(currency || 'INR').toUpperCase() === 'INR'
    ? ['razorpay']
    : ['stripe']
}

export const isTicketInProgress = (booking) => {
  const stage = String(booking?.ticket_status?.stage || '').toLowerCase()
  const status = String(booking?.status || '').toLowerCase()
  const pnr = String(booking?.pnr || booking?.confirmation_code || '').trim()
  const label = String(booking?.ticket_status_label || '').toLowerCase()
  if (status === 'fulfilling' || status === 'payment_received') return true
  if (status === 'confirmed' && !pnr) return true
  return stage.includes('progress') || stage.includes('issuing') || /in progress|issuing ticket/i.test(label)
}

/** My Trips / confirm hero heading — same rules as trips-preview.js `heroCopy`. */
export const tripHeroTitle = (booking) => {
  const status = String(booking?.status || '').toLowerCase()
  const ticketCopy = String(booking?.ticket_status_label || '').trim()
  if (status === 'failed') return booking.status_label || 'Failed'
  if (status === 'refunded' || status.includes('cancel')) {
    return booking.status_label || ticketCopy || 'Trip details'
  }
  if (isTicketInProgress(booking)) return "We're issuing your ticket"
  return ticketCopy || booking.status_label || 'Trip details'
}

export const heroStatusMeta = (statusOrBooking) => {
  const booking = statusOrBooking && typeof statusOrBooking === 'object'
    ? statusOrBooking
    : { status: statusOrBooking }
  const status = String(booking.status || '').toLowerCase()
  const tab = booking.manage_tab
  const cancelled = (
    tab === 'cancelled'
    || status.includes('cancel')
    || status === 'refunded'
    || status === 'failed'
  )
  if (status === 'failed') {
    return { kind: 'is-failed', icon: '✕', title: 'Failed' }
  }
  if (cancelled) {
    return { kind: 'is-cancelled', icon: '✕', title: booking.status_label || 'Cancelled' }
  }
  if (isTicketInProgress(booking)) {
    return { kind: 'is-issuing', icon: '✓', title: "We're issuing your ticket" }
  }
  if (
    status.includes('pending')
    || status === 'payment_received'
    || status === 'draft'
    || status === 'flight_lock'
  ) {
    return { kind: 'is-pending', icon: '!', title: 'Pending' }
  }
  if (status === 'confirmed' || status === 'fulfilling' || booking?.lock?.is_active) {
    return { kind: 'is-confirmed', icon: '✓', title: 'Confirmed' }
  }
  return { kind: 'is-pending', icon: '!', title: status || 'Trip' }
}

/** Keep fare_summary fields needed for Gross sticker + My Trips. */
export const FARE_SUMMARY_KEYS = [
  'base_fare',
  'base_fare_label',
  'taxes_and_fees',
  'taxes_and_fees_label',
  'others',
  'others_label',
  'gross_fare',
  'gross_fare_label',
  'net_fare',
  'net_fare_label',
  'total',
  'total_label',
  'tax_charges',
]

/** Build itemized add-on lines for Fare Details / orders persistence. */
export const buildSsrAddonLines = (selectedAddOns = {}) =>
  Object.values(selectedAddOns || {})
    .filter((a) => a && Number(a.price || a.charge || 0) > 0)
    .map((a) => ({
      label: a.title || a.code || 'Add-on',
      amount: String(a.price ?? a.charge ?? ''),
      amount_label: a.price_label || formatInr(a.price ?? a.charge),
      pax_id: a.paxId || a.pax_id || null,
    }))
